import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  concatMap,
  filter,
  map,
  of,
  tap,
  withLatestFrom,
} from 'rxjs';
import { getUserSuccess } from '../../actions/init.actions';
import { InitFacade } from '../../facade/init.facade';
import * as actions from '../actions/user-management.actions';
import {
  AccountMessage,
  AccountNotification,
  Contacts,
  UserLogout,
} from '../state/user-management.state';
import { UserManagementService } from '../user-management.service';

@Injectable({
  providedIn: 'root',
})
export class UserManagementEffects {
  constructor(
    private actions$: Actions,
    private initFacade: InitFacade,
    private userManagementService: UserManagementService,
  ) {}

  getUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getUserSuccess),
      filter((action) => !!action.accountInfo),
      map((action) => action.accountInfo),
      map((accountInfo) => {
        const label: string = accountInfo?.accountDetails?.email;
        const subLabel: string = `${accountInfo?.accountDetails?.firstName}`;
        return actions.addProfileInfoToDropDownList({
          dropDownOption: {
            id: 0,
            type: 'PROFILE',
            value: '',
            label,
            subLabel,
          },
        });
      }),
    ),
  );

  logOutUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.logOutUser),
      withLatestFrom(this.initFacade.accountInfo$),
      concatMap(([, accountInfo]) => {
        return this.userManagementService.logOutUser(accountInfo.custId).pipe(
          map((loggedOut: UserLogout) => {
            return actions.logOutUserSuccess({ loggedOut });
          }),
          catchError(() => of(actions.logOutUserError())),
        );
      }),
    ),
  );

  logOutUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.logOutUserSuccess),
        tap(() => {
          // Backend already clears auth_token and refreshToken cookies (max_age=0).
          // Redirect to login after confirmed sign-out.
          this.userManagementService.redirectToLogin();
        }),
      ),
    { dispatch: false },
  );

  logOutUserError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.logOutUserError),
        tap(() => {
          // Do NOT redirect on sign-out failure.
          // User stays on the current page so they can retry sign-out from the menu.
        }),
      ),
    { dispatch: false },
  );

  getNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getNotifications),
      withLatestFrom(this.initFacade.accountInfo$),
      concatMap(([action, accountInfo]) => {
        // conditional operator will be removed, after actual service implementation.
        return this.userManagementService
          .getNotifications(
            accountInfo.custId,
            action.offsetLimit,
            action.maxResults,
          )
          .pipe(
            map((notifications: AccountNotification) => {
              return actions.getNotificationsSuccess({
                notifications,
                reload: action.reload,
              });
            }),
            catchError(() => of(actions.getNotificationsError())),
          );
      }),
    ),
  );

  getMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getMessages),
      withLatestFrom(this.initFacade.accountInfo$),
      concatMap(([action, accountInfo]) => {
        // confitional operator will be removed, after actual service implementation.
        return this.userManagementService.getMessages(accountInfo?.custId).pipe(
          map((messages: AccountMessage[]) => {
            return actions.getMessagesSuccess({ messages });
          }),
          catchError(() => of(actions.getMessagesError())),
        );
      }),
    ),
  );

  getContatcs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getContacts),
      withLatestFrom(this.initFacade.accountInfo$),
      concatMap(([, accountInfo]) => {
        return this.userManagementService.getContacts(accountInfo?.custId).pipe(
          map((contacts: Contacts[]) => {
            return actions.getContactsSuccess({ contacts });
          }),
          catchError(() => of(actions.getContactsError())),
        );
      }),
    ),
  );
}
